import {
  FoodDetailsContent,
  FoodDetailsHeader,
  FoodDetailsInfo,
  IngredientList,
  FoodDetailsBody,
  IngredientListTitle,
  CtaMenuPopover,
  CtaOptionText,
} from "./styled"
import { Fragment, useRef, useState } from "react"
import Button from "components/Buttons/Button"
import { useMealDetails } from "../hooks/mealDetailsHooks"
import TrashIcon from "components/Icons/TrashIcon"
import PopConfirm from "components/PopConfirm"
import { useDeleteMeal } from "features/home/hooks"
import { useBackToHome } from "../hooks"
import { ConfirmEntry } from "components/PopConfirm/styled"
import { useSetPageTitle } from "libs/ga"
import { BackButton } from "components/Common/BackButton"
import FullPageLoading from "components/Layouts/FullPageLoading"
import ImagePreviewer from "components/foodDetails/share/FoodImage/ImageViewer"
import FoodListItem from "components/foodItem/FoodListItem"
import { FoodListDivider } from "components/foodItem/FoodListItem/styled"
import { useUpdateMeal } from "../hooks/useUpdateMeal"
import FoodImage from "components/foodDetails/share/FoodImage"
import { FOOD_DETAILS_BODY_ID } from "config"
import { TopNavWrapper } from "components/Layouts/TopNavWrapper"
import ChevronLeftBtn from "components/Icons/ChevronLeftBtn"
import useMealGram from "../hooks/useMealGram"
import {
  isAIScanMeal,
  isAIScanNutritionFactsMeal,
  isAIScanPackageMeal,
  isEdamaMeal,
  isMyMeal,
  isOpenFoodFactMeal,
} from "model/food/utils/typeGuards/mealTypeGuards"
import EdamaNameAndPortion from "components/foodDetails/share/EdamaNameAndPortion"
import MyFoodNameAndPortion from "components/foodDetails/share/MyFoodNameAndPortion"
import CheckIsMealChange from "../hooks/helper/checkIsMealChanged"
import OpenFoodFactNameAndPortion from "components/foodDetails/share/OpenFoodFactNameAndPortion"
import OpenFoodFactDescription from "components/foodDetails/share/OpenFoodFactDescription"
import AIScanNameAndPortion from "components/foodDetails/share/AIScanNameAndPortion"
import FoodDetailsMealTypeSelection from "components/mealTypeSelection/FoodDetailsMealTypeSelection"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"
import { getScheduleStopDate, isFutureDate } from "utils/dateTime"
import { FlexBox } from "components/Common/FlexBox"
import Radio from "components/inputs/Radio"
import { DeleteScheduleType } from "features/schedule/scheduleSlice.types"
import {
  useDeleteSchedule,
  useUpdateScheduleEndDate,
} from "features/schedule/hooks/CRUDSchedule"
import EditableNutrientInformation from "components/foodDetails/share/EditableNutrientInformation"
import EditNutrientForm from "components/foodDetails/share/EditNutrientForm"
import { INutrition } from "model/food/types/base.types"
import { MealType } from "model/food/types"
import useUpdateMealForm from "../hooks/logMeal/useUpdateMealForm"
import HorizontalThreeDots from "components/Icons/HorizontalThreeDots"
import { Action } from "antd-mobile/es/components/action-sheet"
import useCreateFavoriteMeal from "features/favoriteMeals/hooks/useCreateFavoriteMeal"
import { useShowCustomSnackbar } from "features/notification/useShowCustomSnackbar"
import PATHS from "router/paths"
import FavoriteIcon from "components/Icons/FavoriteIcon"
import { SpinLoading } from "antd-mobile"

type DisplaySection = "MEAL_FORM" | "EDIT_NUTRITION_FORM"

const UpdateMeals = () => {
  useSetPageTitle("Edit Meal")
  const [displaySection, setDisplaySection] =
    useState<DisplaySection>("MEAL_FORM")

  const { mealDetails, getMealDetailsLoading } = useMealDetails()
  const {
    meal,
    onPortionChange,
    onUnitChange,
    onMealTypeChange,
    updateMealAsManualNutrition,
  } = useUpdateMealForm({
    initMeal: mealDetails,
    mealType: mealDetails?.type,
  })

  const [isPortionValid, setIsPortionValid] = useState(true)

  const [ctaPopoverVisible, setCtaPopoverVisible] = useState(false)

  const handlePortionChange = (value: number) => {
    onPortionChange(value)
    setIsPortionValid(true)
  }

  const backToHome = useBackToHome()

  const updateMealSuccessCallback =
    displaySection === "EDIT_NUTRITION_FORM"
      ? () => setDisplaySection("MEAL_FORM")
      : backToHome

  const { handleUpdateMeal, updateMealLoading } = useUpdateMeal(
    updateMealSuccessCallback,
  )
  const { handleDeleteMeal, deleteMealLoading } = useDeleteMeal(backToHome)
  const { handleDeleteSchedule, deleteScheduleLoading } =
    useDeleteSchedule(backToHome)

  const { handleUpdateScheduleEndDate, updateScheduleEndDateLoading } =
    useUpdateScheduleEndDate(backToHome)

  const [deleteScheduleType, setDeleteScheduleType] =
    useState<DeleteScheduleType>("This meal")

  const isSubmittingFavorite = useRef(false)

  const handleDeleteScheduleMeal = () => {
    if (!meal?.scheduleId) {
      return
    }

    switch (deleteScheduleType) {
      case "This meal":
        handleDeleteMeal(meal.id)
        break

      case "This and following meals":
        const stopDate = getScheduleStopDate(meal.createdFor!!)

        if (stopDate) {
          handleUpdateScheduleEndDate({
            scheduleId: meal.scheduleId,
            fromDate: stopDate,
          })
        }

        break

      case "All meals":
        handleDeleteSchedule(meal.scheduleId)
        break

      default:
        break
    }
  }

  const previewImageUrl = mealDetails?.previewImageUrl ?? mealDetails?.imageUrl
  const [imagePreviewerVisible, setImagePreviewerVisible] = useState(false)

  const [isPopConfirmDeleteOpen, setIsPopConfirmDeleteOpen] = useState(false)
  const [isPopConfirmDiscardOpen, setIsPopConfirmDiscardOpen] = useState(false)

  const [isManualNutritionUpdated, setIsManualNutritionUpdated] =
    useState(false)

  const gram = useMealGram(meal)

  const hasIngredients = meal && (isMyMeal(meal) || isAIScanMeal(meal))

  const handleBack = () => {
    const isMealChanged = CheckIsMealChange(mealDetails, meal)
    if (isMealChanged || isManualNutritionUpdated) {
      setIsPopConfirmDiscardOpen(true)
    } else {
      backToHome()
    }
  }

  const isFutureScheduleMeal =
    !!mealDetails?.scheduleId && isFutureDate(mealDetails?.createdFor)

  const isDeleteFutureMealLoading =
    deleteMealLoading || deleteScheduleLoading || updateScheduleEndDateLoading

  const handleSubmitUpdateNutrition = ({ gram, ...rest }: INutrition) => {
    if (meal) {
      const updatedMeal: MealType = {
        ...meal,
        ...rest,
        isManualNutrition: true,
      }

      updateMealAsManualNutrition(updatedMeal)
      setIsManualNutritionUpdated(true)
    }
  }

  const handleSubmitUpdateMeal = () => {
    if (meal) {
      handleUpdateMeal({
        ...meal,
        isManualNutrition: meal.isManualNutrition ?? false,
      })
    }
  }

  const { handleShowCustomSnackbar } = useShowCustomSnackbar()

  const { handleCreateFavoriteMeal, createFavoriteMealLoading } =
    useCreateFavoriteMeal(() => {
      handleShowCustomSnackbar({
        type: "success",
        status: "active",
        title: "Food has been added to favorites.",
        link: {
          text: "View Favorites",
          src: PATHS.app.myFoods.root,
        },
      })
      setCtaPopoverVisible(false)
      isSubmittingFavorite.current = false
    })

  const onSubmitCreateFavoriteMeal = () => {
    if (meal) {
      handleCreateFavoriteMeal({
        ...meal,
        isManualNutrition: meal.isManualNutrition ?? false,
      })
    }
  }

  const onSubmit = () => {
    handleSubmitUpdateMeal()
  }

  const actions: Action[] = [
    {
      key: "favorite",
      text: (
        <FlexBox $alignItems="center" $gap={8}>
          {createFavoriteMealLoading ? (
            <SpinLoading style={{ "--size": "16px" }} />
          ) : (
            <FavoriteIcon />
          )}
          <CtaOptionText>Add to Favorites</CtaOptionText>
        </FlexBox>
      ),
    },
    {
      key: "delete",
      text: (
        <FlexBox $alignItems="center" $gap={8}>
          <TrashIcon color="#FF4921" /> <CtaOptionText>Delete</CtaOptionText>
        </FlexBox>
      ),
    },
  ]

  if (displaySection === "EDIT_NUTRITION_FORM" && meal) {
    return (
      <EditNutrientForm
        onSubmit={handleSubmitUpdateNutrition}
        meal={meal}
        onClose={() => {
          setDisplaySection("MEAL_FORM")
        }}
      />
    )
  }

  return (
    <Fragment>
      {getMealDetailsLoading || !meal ? (
        <FullPageLoading />
      ) : (
        <Fragment>
          <ImagePreviewer
            isOpen={imagePreviewerVisible}
            onClose={() => {
              setImagePreviewerVisible(false)
            }}
            imageUrl={previewImageUrl}
          />

          <FoodDetailsContent>
            <FoodDetailsBody id={FOOD_DETAILS_BODY_ID}>
              <FoodImage previewImageUrl={meal.previewImageUrl}>
                <TopNavWrapper>
                  <FoodDetailsHeader>
                    <BackButton onClick={handleBack}>
                      <ChevronLeftBtn />
                    </BackButton>

                    <FoodDetailsMealTypeSelection
                      value={meal.type}
                      onChange={(val) => onMealTypeChange(val)}
                    />
                    <div style={{ width: 24, height: 24 }}></div>
                  </FoodDetailsHeader>
                </TopNavWrapper>
              </FoodImage>

              <FoodDetailsInfo>
                {isMyMeal(meal) && (
                  <MyFoodNameAndPortion
                    food={meal}
                    portionValue={meal.portion}
                    unitValue={meal.unit}
                    onPortionChange={handlePortionChange}
                    onUnitChange={onUnitChange}
                    disabled={getMealDetailsLoading}
                    setIsPortionValid={setIsPortionValid}
                    calories={meal.calorie}
                    gram={gram}
                    isFutureScheduleMeal={isFutureScheduleMeal}
                  />
                )}

                {(isAIScanMeal(meal) ||
                  isAIScanPackageMeal(meal) ||
                  isAIScanNutritionFactsMeal(meal)) && (
                  <AIScanNameAndPortion
                    food={meal}
                    portionValue={meal.portion}
                    unitValue={meal.unit}
                    onPortionChange={handlePortionChange}
                    onUnitChange={onUnitChange}
                    disabled={getMealDetailsLoading}
                    setIsPortionValid={setIsPortionValid}
                    calories={meal.calorie}
                    gram={gram}
                    isFutureScheduleMeal={isFutureScheduleMeal}
                  />
                )}

                {isEdamaMeal(meal) && (
                  <EdamaNameAndPortion
                    edama={meal}
                    portionValue={meal.portion}
                    unitValue={meal.unit}
                    onPortionChange={handlePortionChange}
                    onUnitChange={onUnitChange}
                    disabled={getMealDetailsLoading}
                    setIsPortionValid={setIsPortionValid}
                    calories={meal.calorie}
                    gram={gram}
                    unitOptions={meal.units}
                    isFutureScheduleMeal={isFutureScheduleMeal}
                  />
                )}

                {isOpenFoodFactMeal(meal) && (
                  <OpenFoodFactNameAndPortion
                    food={meal}
                    portionValue={meal.portion}
                    unitValue={meal.unit}
                    onPortionChange={handlePortionChange}
                    disabled={getMealDetailsLoading}
                    setIsPortionValid={setIsPortionValid}
                    calories={meal.calorie}
                    gram={gram}
                    isFutureScheduleMeal={isFutureScheduleMeal}
                  />
                )}

                <EditableNutrientInformation
                  entry={meal}
                  setEditMode={() => setDisplaySection("EDIT_NUTRITION_FORM")}
                />

                {hasIngredients ? (
                  <IngredientList>
                    <IngredientListTitle>Ingredients</IngredientListTitle>{" "}
                    {meal.ingredients.map((ingredient, index) => (
                      <div key={index}>
                        <FoodListItem
                          isIngredient
                          food={ingredient}
                          hideAddBtn
                          style={{ padding: "6px 0px" }}
                          isManualNutrition={meal.isManualNutrition}
                        />
                        {index !== (meal.ingredients?.length ?? 1) - 1 && (
                          <FoodListDivider />
                        )}
                      </div>
                    ))}
                  </IngredientList>
                ) : undefined}

                {isOpenFoodFactMeal(meal) && <OpenFoodFactDescription />}
              </FoodDetailsInfo>
            </FoodDetailsBody>

            <AppStyleCtaBox>
              <CtaMenuPopover
                onVisibleChange={(visible) => {
                  if (!isSubmittingFavorite.current) {
                    setCtaPopoverVisible(visible)
                  }
                }}
                visible={ctaPopoverVisible}
                style={{ opacity: createFavoriteMealLoading ? 0.6 : 1 }}
                actions={actions}
                placement="top-start"
                onAction={({ key }) => {
                  if (key === "favorite") {
                    onSubmitCreateFavoriteMeal()
                    isSubmittingFavorite.current = true
                    return
                  }

                  if (key === "delete") {
                    setIsPopConfirmDeleteOpen(true)
                  }
                }}
                trigger="click"
              >
                <Button
                  onClick={() => setCtaPopoverVisible(true)}
                  style={{
                    padding: "0 15px",
                    height: 48,
                    borderColor: "rgba(0, 38, 58, 0.14)",
                  }}
                  $isOption={true}
                  $size="small"
                  $type="default"
                >
                  <HorizontalThreeDots />
                </Button>
              </CtaMenuPopover>

              <Button
                loading={updateMealLoading}
                $type="primary"
                style={{ width: "100%" }}
                disabled={!isPortionValid}
                onClick={onSubmit}
              >
                Save
              </Button>
            </AppStyleCtaBox>
          </FoodDetailsContent>

          {isFutureScheduleMeal ? (
            <PopConfirm
              isOpen={isPopConfirmDeleteOpen}
              title="Delete Schedule meal?"
              onCancel={() => setIsPopConfirmDeleteOpen(false)}
              onConfirm={() => {
                handleDeleteScheduleMeal()
              }}
              type="danger"
              loading={isDeleteFutureMealLoading}
            >
              <FlexBox $gap={8} $direction="column">
                <Radio
                  label="This meal"
                  checked={deleteScheduleType === "This meal"}
                  onChange={() => setDeleteScheduleType("This meal")}
                />
                <Radio
                  label="This and following meals"
                  checked={deleteScheduleType === "This and following meals"}
                  onChange={() =>
                    setDeleteScheduleType("This and following meals")
                  }
                />
                <Radio
                  label="All meals"
                  checked={deleteScheduleType === "All meals"}
                  onChange={() => setDeleteScheduleType("All meals")}
                />
              </FlexBox>
            </PopConfirm>
          ) : (
            <PopConfirm
              isOpen={isPopConfirmDeleteOpen}
              title="Delete meal?"
              onCancel={() => setIsPopConfirmDeleteOpen(false)}
              onConfirm={() => {
                handleDeleteMeal(meal.id)
              }}
              type="danger"
              loading={deleteMealLoading}
            >
              <div style={{ textAlign: "center" }}>
                You are about to delete <ConfirmEntry>{meal.name}</ConfirmEntry>
                . Are you sure?
              </div>
            </PopConfirm>
          )}

          <PopConfirm
            isOpen={isPopConfirmDiscardOpen}
            title="Unsaved changes?"
            cancelText="Discard"
            confirmText="Save changes"
            onCancel={() => backToHome()}
            onConfirm={handleSubmitUpdateMeal}
            loading={updateMealLoading}
          >
            <p style={{ textAlign: "center" }}>
              Do you want to save the changes?
            </p>
          </PopConfirm>
        </Fragment>
      )}
    </Fragment>
  )
}

export default UpdateMeals
